import React, { useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useParams, useLocation } from "react-router";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { trans } from "sharedKernel/i18n";
import { trackService } from 'data/services/trackService';
import { showToast } from 'components/toast';
import { loggedUser } from 'sharedKernel/user/LoggedUser';
import {
  Wrapper,
  SidebarCard,
  LessonList,
  LessonItem,
  PlayBadge,
  Sticky,
  Section,
  LessonPageOuter,
  LessonMainColumn,
  LessonSidebarColumn,
  SidebarProgressSection,
  SidebarDivider,
  SidebarLessonList,
  MobileProgressSection,
  LessonCompleteRow,
} from "../../startup/details/trackAvailableDetailsPage";
import { useTranslation } from 'react-i18next';
import { Loading } from "components/loading";
import DOMPurify from "dompurify";
import { VideoPlayer } from "components/VideoPlayer";
import { Message } from "rsuite";

type Lesson = {
  id: string | number;
  title: string;
  minutes: number;
  index: number;
  sequence: number;
  videoId?: string;
  embeddedVideoUrl: string;
  description?: string;
};

type SectionT = {
  id: string | number;
  title: string;
  lessons: Lesson[]
};

type TrackDetails = {
  id: string | number;
  title: string;
  coverUrl?: string;
  imageUrl?: string;
  totalMinutes: number;
  totalLessons: number;
  description: string[];
  sections: SectionT[];
};

export const LearningLessonPage: React.FC = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [trackData, setTrackData] = useState<any>(null);
  const [lessonData, setLessonData] = useState<Lesson | null>(null);
  const [progressData, setProgressData] = useState<any>(null);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [currentLessonStatus, setCurrentLessonStatus] = useState<"NotStarted" | "InProgress" | "Completed">("NotStarted");

  const getSubscriptionId = async (trackId: string, progressDetails?: any) => {

    try {
      const currentUser = loggedUser.get();
      const trackDetails = await trackService.getTrackDetails(trackId);

      if (trackDetails.subscriptions && trackDetails.subscriptions.length > 0) {
        const currentUserSubscription = trackDetails.subscriptions.find((sub: any) => {

          return sub.companyId === currentUser.company?.id ||
            sub.appUserId === currentUser.company?.id ||
            sub.id === currentUser.company?.id;
        });

        if (currentUserSubscription) {
          setCurrentSubscriptionId(currentUserSubscription.id);
          return;
        }
      }

      if (progressDetails && progressDetails.rows && progressDetails.rows.length > 0) {
        const currentUserSubscription = progressDetails.rows.find((sub: any) => {
          return sub.appUserId === currentUser.company?.id ||
            sub.appUserName === currentUser.name ||
            sub.subscriptionId === currentUser.company?.id;
        });

        if (currentUserSubscription) {
          setCurrentSubscriptionId(currentUserSubscription.subscriptionId);
        } else {
          console.warn('Usuário atual não encontrado nas inscrições, usando primeiro item');
          setCurrentSubscriptionId(progressDetails.rows[0].subscriptionId);
        }
      } else {
        console.warn('Nenhuma inscrição encontrada para a trilha');
      }
    } catch (error) {
    }
  };

  const calculateTrackProgress = (lessonProgresses: any[]) => {
    if (!lessonProgresses || lessonProgresses.length === 0) return 0;

    const totalLessons = lessonProgresses.length;
    const completedLessons = lessonProgresses.filter(lp => lp.status === 'Completed').length;
    const currentLesson = lessonProgresses.find(lp => lp.status === 'InProgress');
    const currentLessonProgress = currentLesson ? currentLesson.progress / 100 : 0;

    const totalProgress = ((completedLessons + currentLessonProgress) / totalLessons) * 100;
    return Math.round(totalProgress * 100) / 100;
  };

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://customer-tv6a4xnmx32jrk21.cloudflarestream.com') {
        return;
      }

      if (event.data && event.data.type === 'video-play' && currentLessonStatus === 'NotStarted') {
        updateLessonStatus('InProgress');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentLessonStatus]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const myLearningTracks = await trackService.getMyLearning();

        if (myLearningTracks.rows && myLearningTracks.rows.length > 0) {
          for (const track of myLearningTracks.rows) {
            try {
              const trackDetails = await trackService.getTrackDetails(track.id);

              if (trackDetails.lessons) {
                const lesson = trackDetails.lessons.find(l => l.id === lessonId);

                if (lesson) {
                  setTrackData(trackDetails);
                  setLessonData({
                    id: lesson.id,
                    title: lesson.title,
                    minutes: lesson.minutes,
                    index: lesson.sequence,
                    sequence: lesson.sequence,
                    videoId: lesson.videoId || undefined,
                    embeddedVideoUrl: lesson.embeddedVideoUrl,
                    description: lesson.description
                  });

                  try {
                    const progressDetails = await trackService.getTrackSubscriptions(track.id);
                    setProgressData(progressDetails);

                    await getSubscriptionId(track.id, progressDetails);

                    if (!currentSubscriptionId && trackDetails.subscriptions && trackDetails.subscriptions.length > 0) {
                      const firstSubscription = trackDetails.subscriptions[0];
                      setCurrentSubscriptionId(firstSubscription.id);
                    }

                    if (lessonId && lesson.userProgressStatus) {
                      setCurrentLessonStatus(lesson.userProgressStatus);
                    } else if (lessonId && (progressDetails as any)?.lessonProgresses) {
                      const lessonProgress = (progressDetails as any).lessonProgresses.find((lp: any) => lp.lessonId === lessonId);
                      const initialStatus = lessonProgress ? lessonProgress.status : 'NotStarted';
                      setCurrentLessonStatus(initialStatus);
                    }

                    if (!lesson.videoId) {
                      const currentStatus = lesson.userProgressStatus || 'NotStarted';
                      if (currentStatus === 'NotStarted') {
                        setTimeout(() => {
                          updateLessonStatus('InProgress');
                        }, 500);
                      }
                    }
                  } catch (progressError) {
                    console.warn('Erro ao buscar progresso:', progressError);
                  }

                  setTimeout(() => window.dispatchEvent(new Event('trackNameChanged')), 0);

                  break;
                }
              }
            } catch (error) {
              console.error('Erro ao buscar detalhes da trilha:', error);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [lessonId]);

  const navigateToLesson = async (lesson: Lesson) => {
    if (!trackData) return;
    if (lesson.id === lessonId) {
      setLessonData({
        id: lesson.id,
        title: lesson.title,
        minutes: lesson.minutes,
        index: lesson.sequence,
        sequence: lesson.sequence,
        videoId: lesson.videoId,
        embeddedVideoUrl: lesson.embeddedVideoUrl,
        description: lesson.description
      });

      // Se for uma aula sem vídeo e ainda não iniciada, marcar como "Em andamento"
      if (!lesson.videoId && currentLessonStatus === 'NotStarted') {
        updateLessonStatus('InProgress');
      }

    } else {
      navigate(`/hub/learning/lesson/${lesson.id}`, {
        state: {
          lesson: lesson,
          track: trackData
        }
      });
    }
  };

  const getLessonStatus = (lesson: any) => {
    if (lesson.id === lessonId) {
      switch (currentLessonStatus) {
        case 'Completed':
          return 'concluida';
        case 'InProgress':
          return 'em-progresso';
        case 'NotStarted':
        default:
          return 'nao-iniciada';
      }
    }

    const status = lesson.userProgressStatus || 'NotStarted';

    switch (status) {
      case 'Completed':
        return 'concluida';
      case 'InProgress':
        return 'em-progresso';
      case 'NotStarted':
      default:
        return 'nao-iniciada';
    }
  };

  const getProgressPercentage = () => {
    if (trackData && trackData.userProgress !== undefined) {
      return Math.round(trackData.userProgress);
    }
    return 0;
  };

  const getCompletedLessons = () => {
    if (trackData && trackData.userCompletedLessons !== undefined) {
      return trackData.userCompletedLessons;
    }
    return 0;
  };

  const resolveSubscriptionId = async (): Promise<string | null> => {
    if (!trackData) {
      return currentSubscriptionId || null;
    }
    if (currentSubscriptionId) {
      return currentSubscriptionId;
    }
    const immediate = (trackData as any).subscriptions?.[0]?.id
      || (progressData as any)?.rows?.[0]?.subscriptionId
      || null;
    if (immediate) {
      setCurrentSubscriptionId(immediate);
      return immediate;
    }
    try {
      await getSubscriptionId(trackData.id, progressData);
    } catch (error) {
      console.error('Erro ao buscar subscription ID:', error);
    }
    const finalId = currentSubscriptionId
      || (trackData as any).subscriptions?.[0]?.id
      || (progressData as any)?.rows?.[0]?.subscriptionId
      || null;
    return finalId;
  };

  const updateLessonStatus = async (status: "InProgress" | "Completed") => {
    if (!lessonId) return;
    const subId = await resolveSubscriptionId();
    if (!subId) {
      showToast('error', 'Não foi possível identificar sua inscrição para atualizar o progresso.');
      return;
    }

    try {
      setIsUpdatingProgress(true);
      const result = await trackService.updateLessonProgress(lessonId, subId, status);

      setCurrentLessonStatus(status);
      if (trackData && trackData.lessons) {
        const updatedLessons = trackData.lessons.map((lesson: any) =>
          lesson.id === lessonId ? { ...lesson, userProgressStatus: status } : lesson
        );

        const completedLessons = updatedLessons.filter((lesson: any) => lesson.userProgressStatus === 'Completed').length;
        const totalLessons = updatedLessons.length;
        const newProgress = Math.round((completedLessons / totalLessons) * 100);

        setTrackData({
          ...trackData,
          lessons: updatedLessons,
          userProgress: newProgress,
          userCompletedLessons: completedLessons
        });
      }

      if (progressData && progressData.lessonProgresses) {
        const updatedProgresses = progressData.lessonProgresses.map((lp: any) =>
          lp.lessonId === lessonId ? { ...lp, status } : lp
        );
        setProgressData({ ...progressData, lessonProgresses: updatedProgresses });
      } else {
        const newLessonProgress = {
          lessonId: lessonId,
          status: status,
          progress: status === 'Completed' ? 100 : 0
        };

        setProgressData(prev => ({
          ...prev,
          lessonProgresses: [newLessonProgress]
        }));
      }

      if (status === 'Completed') {
        showToast('success', 'Aula marcada como concluída!');
      }
    } catch (error: any) {
      showToast('error', 'Erro ao atualizar progresso da aula');
    } finally {
      setIsUpdatingProgress(false);
    }
  };


  const handleMarkAsCompleted = () => {
    updateLessonStatus('Completed');
  };

  if (isLoading) {
    return (
      <Wrapper>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <Loading />
        </div>
      </Wrapper>
    );
  }

  if (!trackData || !lessonData) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-3">
          <Col>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/hub/learning')}
              className="mb-3"
            >
              <FontAwesomeIcon icon={["fas", "arrow-left"]} className="me-2" />
              {trans("Voltar")}
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="text-center">
              <h2>{trans("Aula não encontrada")}</h2>
              <p className="text-muted">
                {trans("Esta aula não foi encontrada em suas trilhas de aprendizado")}
              </p>
            </div>
          </Col>
        </Row>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div data-track-name style={{ display: 'none' }}>{trackData?.title || 'Carregando...'}</div>
      <LessonPageOuter>
        <LessonMainColumn>
          <div className="mb-4">
            <h1 className="h3 fw-bold">Aula {lessonData.index} - {lessonData.title}</h1>
          </div>
          {lessonData.videoId ? (
            <>
              <div className="mb-4">
                <div
                  className="bg-dark text-white position-relative"
                  style={{ height: '500px', borderRadius: '12px', overflow: 'hidden' }}
                >
                  <div
                    style={{ width: '100%', height: '100%', position: 'relative' }}
                  >
                    <iframe
                      src={lessonData.embeddedVideoUrl}
                      title={lessonData.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: '12px'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                    />
                    {currentLessonStatus === 'NotStarted' && (
                      <button
                        type="button"
                        onClick={() => updateLessonStatus('InProgress')}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: 'pointer'
                        }}
                        aria-label="Iniciar reprodução"
                      />
                    )}
                  </div>
                </div>
              </div>

              <MobileProgressSection>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: '#6c757d' }}>Progresso</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1365A3' }}>{getProgressPercentage()}%</span>
                </div>
                <div className="progress mb-1" style={{ height: '6px', backgroundColor: '#e9ecef' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${getProgressPercentage()}%`, backgroundColor: '#1365A3' }}
                    aria-valuenow={getProgressPercentage()}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <small style={{ color: '#6c757d' }}>{getCompletedLessons()}/{trackData?.lessons?.length || 0} aulas</small>
              </MobileProgressSection>

              <LessonCompleteRow>
                {currentLessonStatus === 'Completed' ? (
                  <div className="d-flex align-items-center text-success">
                    <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2" />
                    <span className="fw-semibold">Aula Concluída</span>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleMarkAsCompleted}
                    disabled={isUpdatingProgress || currentLessonStatus === 'NotStarted'}
                    style={{
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      minWidth: '180px'
                    }}
                  >
                    {isUpdatingProgress ? (
                      <>
                        <FontAwesomeIcon icon={["fas", "spinner"]} spin className="me-2" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2" />
                        Marcar como Concluída
                      </>
                    )}
                  </Button>
                )}
              </LessonCompleteRow>

              <div className="mt-3">
                <Section style={{ background: 'transparent', boxShadow: 'none', border: 'none' }}>
                  <div className="body" style={{ background: 'transparent' }}>
                    <h3 className="mb-3" style={{ fontSize: 20, fontWeight: 700 }}>
                      {t("Visão Geral")}
                    </h3>
                    <hr />
                    <div
                      style={{ lineHeight: 1.7, marginBottom: 12, fontSize: 16 }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          lessonData.description ||
                          "Esta aula faz parte da trilha de aprendizado e aborda conceitos importantes para seu desenvolvimento. " +
                          "Assista ao vídeo com atenção e pratique os exercícios propostos."
                        )
                      }}
                    />
                  </div>
                </Section>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <Section style={{ background: 'transparent', boxShadow: 'none', border: 'none' }}>
                  <div className="body" style={{ background: 'transparent' }}>
                    <div
                      style={{ lineHeight: 1.7, marginBottom: 12, fontSize: 16 }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          lessonData.description ||
                          "Esta aula faz parte da trilha de aprendizado e aborda conceitos importantes para seu desenvolvimento."
                        )
                      }}
                    />
                  </div>
                </Section>
              </div>

              <MobileProgressSection>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: '#6c757d' }}>Progresso</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1365A3' }}>{getProgressPercentage()}%</span>
                </div>
                <div className="progress mb-1" style={{ height: '6px', backgroundColor: '#e9ecef' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${getProgressPercentage()}%`, backgroundColor: '#1365A3' }}
                    aria-valuenow={getProgressPercentage()}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <small style={{ color: '#6c757d' }}>{getCompletedLessons()}/{trackData?.lessons?.length || 0} aulas</small>
              </MobileProgressSection>

              <LessonCompleteRow>
                {currentLessonStatus === 'Completed' ? (
                  <div className="d-flex align-items-center text-success">
                    <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2" />
                    <span className="fw-semibold">Aula Concluída</span>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleMarkAsCompleted}
                    disabled={isUpdatingProgress || currentLessonStatus === 'NotStarted'}
                    style={{
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      minWidth: '180px'
                    }}
                  >
                    {isUpdatingProgress ? (
                      <>
                        <FontAwesomeIcon icon={["fas", "spinner"]} spin className="me-2" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={["fas", "check-circle"]} className="me-2" />
                        Marcar como Concluída
                      </>
                    )}
                  </Button>
                )}
              </LessonCompleteRow>
            </>
          )}
        </LessonMainColumn>

        <LessonSidebarColumn>
          <div className="text-center mb-4">
            <div className="d-flex align-items-center justify-content-center mb-3">
              <h6 className="mb-0 fw-bold me-2">Aulas</h6>
            </div>

            <SidebarProgressSection>
              <div className="position-relative mb-4" style={{ marginTop: '40px' }}>
                <div
                  className="position-absolute"
                  style={{
                    left: `${getProgressPercentage()}%`,
                    transform: 'translateX(-50%)',
                    top: '-35px',
                    backgroundColor: '#c5e6ff',
                    color: '#1365A3',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    zIndex: 10
                  }}
                >
                  {getProgressPercentage()}%
                  <div
                    className="position-absolute"
                    style={{
                      bottom: '-4px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid #c5e6ff'
                    }}
                  ></div>
                </div>
                <div className="progress" style={{ width: '100%', height: '6px', backgroundColor: '#e9ecef' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${getProgressPercentage()}%`,
                      backgroundColor: '#c5e6ff'
                    }}
                    aria-valuenow={getProgressPercentage()}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
              </div>

              <small className="text-muted">{getCompletedLessons()}/{trackData?.lessons?.length || 0} aulas</small>
            </SidebarProgressSection>
          </div>

          <SidebarDivider />

          <SidebarLessonList>
            {trackData?.lessons?.map((lesson: any) => {
              const status = getLessonStatus(lesson);

              return (
                <div
                  key={lesson.id}
                  className="mb-3 p-3 cursor-pointer"
                  style={{
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderBottom: '1px solid #dee2e6'
                  }}
                  onClick={() => navigateToLesson({
                    id: lesson.id,
                    title: lesson.title,
                    minutes: lesson.minutes,
                    index: lesson.sequence,
                    sequence: lesson.sequence,
                    videoId: lesson.videoId,
                    embeddedVideoUrl: lesson.embeddedVideoUrl,
                    description: lesson.description
                  })}
                >
                  <div className="d-flex align-items-center mb-2">
                    <span
                      className="badge me-2"
                      style={{
                        fontSize: '10px',
                        fontWeight: 'normal',
                        backgroundColor: status === 'concluida' ? '#dcffe0' :
                          status === 'em-progresso' ? '#ffe7d1' :
                            '#f8f9fa',
                        color: status === 'concluida' ? '#219D1D' :
                          status === 'em-progresso' ? '#A95100' :
                            '#6c757d'
                      }}
                    >
                      {status === 'concluida' ? 'Finalizada' :
                        status === 'em-progresso' ? 'Em progresso' :
                          'Não iniciada'}
                    </span>
                  </div>
                  <h6 className="mb-1" style={{ fontSize: '14px', fontWeight: 600 }}>
                    {lesson.title}
                  </h6>
                  <small className="text-muted d-flex align-items-center">
                    {lesson.videoId ? (
                      <>
                        <img src="/video-library.svg" alt="video" width={14} height={14} className="me-1" />
                        Aula {lesson.sequence} • {lesson.minutes} minutos
                      </>
                    ) : (
                      <>
                        <img src="/library-books.svg" alt="aula" width={14} height={14} className="me-1" />
                        Aula {lesson.sequence}
                      </>
                    )}
                  </small>
                </div>
              );
            })}
          </SidebarLessonList>
        </LessonSidebarColumn>
      </LessonPageOuter>
    </Wrapper>
  );
};
